import Vue from 'vue'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from '../layouts/error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_sentryserver_5e1d532e from 'nuxt_plugin_sentryserver_5e1d532e' // Source: ./sentry.server.js (mode: 'server')
import nuxt_plugin_sentryclient_0f383cb4 from 'nuxt_plugin_sentryclient_0f383cb4' // Source: ./sentry.client.js (mode: 'client')
import nuxt_plugin_router_6b5d9b85 from 'nuxt_plugin_router_6b5d9b85' // Source: ./router.js (mode: 'all')
import nuxt_plugin_recaptcha_7dce66d9 from 'nuxt_plugin_recaptcha_7dce66d9' // Source: ./recaptcha.js (mode: 'all')
import nuxt_plugin_axios_71429d6a from 'nuxt_plugin_axios_71429d6a' // Source: ./axios.js (mode: 'all')
import nuxt_plugin_i18n_559c2e38 from 'nuxt_plugin_i18n_559c2e38' // Source: ../plugins/i18n (mode: 'all')
import nuxt_plugin_vuelidate_cdc23eaa from 'nuxt_plugin_vuelidate_cdc23eaa' // Source: ../plugins/vuelidate (mode: 'all')
import nuxt_plugin_notifications_075e64e2 from 'nuxt_plugin_notifications_075e64e2' // Source: ../plugins/notifications (mode: 'all')
import nuxt_plugin_polyfill_b20739d6 from 'nuxt_plugin_polyfill_b20739d6' // Source: ../plugins/polyfill (mode: 'all')
import nuxt_plugin_axios_5e891320 from 'nuxt_plugin_axios_5e891320' // Source: ../plugins/axios (mode: 'all')
import nuxt_plugin_vuetify_3d7487f0 from 'nuxt_plugin_vuetify_3d7487f0' // Source: ../plugins/vuetify (mode: 'all')
import nuxt_plugin_gtag_559bfece from 'nuxt_plugin_gtag_559bfece' // Source: ../plugins/gtag (mode: 'all')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":true,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

async function createApp (ssrContext) {
  const router = await createRouter(ssrContext)

  const store = createStore(ssrContext)
  // Add this.$router into store actions/mutations
  store.$router = router

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    head: {"title":"Vzdělávání lékařů","meta":[{"charset":"utf-8"},{"name":"viewport","content":"width=device-width, initial-scale=1"},{"hid":"description","name":"description","content":"Czech Medical Chamber educational server"},{"name":"Content-Security-Policy","content":"default-src 'self'; script-src 'self' *.google-analytics.com *.analytics.google.com  *.googletagmanager.com https:\u002F\u002Fssl.google-analytics.com 'unsafe-inline'; img-src 'self' *.google-analytics.com *.analytics.google.com  *.googletagmanager.com; connect-src *.google-analytics.com *.analytics.google.com  *.googletagmanager.com"}],"link":[{"rel":"icon","type":"image\u002Fx-icon","href":"\u002Ffavicon.ico"}],"script":[{"src":"\u002Fjs\u002Fswiper.min.js","type":"text\u002Fjavascript"},{"src":"https:\u002F\u002Fsource.zoom.us\u002F1.8.1\u002Flib\u002Fvendor\u002Fjquery.min.js","type":"text\u002Fjavascript"}],"style":[]},

    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        let nuxt = app.nuxt // to work with @vue/composition-api, see https://github.com/nuxt/nuxt.js/issues/6517#issuecomment-573280207
        if (this) {
          nuxt = this.nuxt || this.$options.nuxt
        }
        nuxt.dateErr = Date.now()
        nuxt.err = err
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    ssrContext
  })

  const inject = function (key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error(`inject('${key}', value) has no value provided`)
    }

    key = '$' + key
    // Add into app
    app[key] = value

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Plugin execution

  if (process.server && typeof nuxt_plugin_sentryserver_5e1d532e === 'function') {
    await nuxt_plugin_sentryserver_5e1d532e(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_sentryclient_0f383cb4 === 'function') {
    await nuxt_plugin_sentryclient_0f383cb4(app.context, inject)
  }

  if (typeof nuxt_plugin_router_6b5d9b85 === 'function') {
    await nuxt_plugin_router_6b5d9b85(app.context, inject)
  }

  if (typeof nuxt_plugin_recaptcha_7dce66d9 === 'function') {
    await nuxt_plugin_recaptcha_7dce66d9(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_71429d6a === 'function') {
    await nuxt_plugin_axios_71429d6a(app.context, inject)
  }

  if (typeof nuxt_plugin_i18n_559c2e38 === 'function') {
    await nuxt_plugin_i18n_559c2e38(app.context, inject)
  }

  if (typeof nuxt_plugin_vuelidate_cdc23eaa === 'function') {
    await nuxt_plugin_vuelidate_cdc23eaa(app.context, inject)
  }

  if (typeof nuxt_plugin_notifications_075e64e2 === 'function') {
    await nuxt_plugin_notifications_075e64e2(app.context, inject)
  }

  if (typeof nuxt_plugin_polyfill_b20739d6 === 'function') {
    await nuxt_plugin_polyfill_b20739d6(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_5e891320 === 'function') {
    await nuxt_plugin_axios_5e891320(app.context, inject)
  }

  if (typeof nuxt_plugin_vuetify_3d7487f0 === 'function') {
    await nuxt_plugin_vuetify_3d7487f0(app.context, inject)
  }

  if (typeof nuxt_plugin_gtag_559bfece === 'function') {
    await nuxt_plugin_gtag_559bfece(app.context, inject)
  }

  // If server-side, wait for async component to be resolved first
  if (process.server && ssrContext && ssrContext.url) {
    await new Promise((resolve, reject) => {
      router.push(ssrContext.url, resolve, () => {
        // navigated to a different route in router guard
        const unregister = router.afterEach(async (to, from, next) => {
          ssrContext.url = to.fullPath
          app.context.route = await getRouteData(to)
          app.context.params = to.params || {}
          app.context.query = to.query || {}
          unregister()
          resolve()
        })
      })
    })
  }

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
